import { useState } from "react";
import { api } from "../../../lib/apiClient";
import { ig, Badge } from "../inbox/_inbox_shared";
import { X, Sparkles, ChevronRight } from "lucide-react";

const SUGGESTIONS = [
  "Create a workflow for new funding leads that sends a welcome SMS, waits 5 minutes, sends the intake form, creates an opportunity, then follows up in Haitian Creole if they don't book.",
  "When a contact replies to an appointment reminder, mark them confirmed; if no reply within 2 hours, send another SMS.",
  "If a ticket is set to urgent, assign senior agent, notify manager on Slack, send client acknowledgement, create a 24h follow-up task.",
];

export default function BuildWithAIModal({ onClose, onCreated }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState(false);

  const generate = async () => {
    if (!prompt.trim()) return;
    setBusy(true); setErr(""); setPlan(null);
    try {
      const r = await api.post("/api/automation/build-ai", { prompt });
      setPlan(r);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const createWorkflow = async () => {
    if (!plan) return;
    setCreating(true);
    try {
      const wf = await api.post("/api/automation/workflows", {
        name: plan.name, description: plan.description,
        nodes: plan.nodes, edges: plan.edges,
      });
      onCreated(wf.id);
    } catch (e) { setErr(e.message); }
    finally { setCreating(false); }
  };

  return (
    <div onClick={onClose} data-testid="ai-modal" style={{
      position: "fixed", inset: 0, background: "rgba(7,31,24,0.4)", zIndex: 100,
      display: "grid", placeItems: "center", fontFamily: "Inter, sans-serif",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 720, maxWidth: "92vw", maxHeight: "88vh", overflow: "hidden",
        background: ig.card, borderRadius: 18, boxShadow: "0 20px 60px rgba(7,31,24,0.4)",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${ig.border}`, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{
            width: 38, height: 38, borderRadius: 10, display: "grid", placeItems: "center",
            background: "linear-gradient(135deg, #7C3AED, #C084FC)", color: "#fff",
          }}><Sparkles size={18} /></span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Build using AI</div>
            <div style={{ fontSize: 11.5, color: ig.muted, marginTop: 2 }}>Describe the automation in plain English and Claude will draft it for you.</div>
          </div>
          <button onClick={onClose} style={iconBtn}><X size={14} /></button>
        </div>

        <div className="ig-scroll" style={{ flex: 1, overflowY: "auto", padding: 22 }}>
          <div style={{ fontSize: 10.5, color: ig.muted, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 6 }}>
            What should this workflow do?
          </div>
          <textarea data-testid="ai-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
            placeholder="e.g. When a contact submits the funding form, send a welcome SMS, wait 5 minutes, send the intake form via email, create an opportunity, wait 1 day, if no appointment is booked send a follow-up in Haitian Creole."
            style={{
              width: "100%", padding: 14, borderRadius: 12, border: `1px solid ${ig.border}`,
              background: ig.bg, fontSize: 13.5, lineHeight: 1.55, fontFamily: "inherit", outline: "none", color: ig.ink, resize: "vertical",
            }} />

          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
            {SUGGESTIONS.map((s, i) => (
              <button key={i} data-testid={`ai-suggest-${i}`} onClick={() => setPrompt(s)} style={{
                padding: "7px 11px", borderRadius: 999, border: `1px solid #DDD6FE`, background: "#F5F3FF",
                color: "#6D28D9", fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{s.slice(0, 60)}…</button>
            ))}
          </div>

          {err && <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "#FEE2E2", color: "#991B1B", fontSize: 12 }}>{err}</div>}

          {plan && (
            <div style={{ marginTop: 20, padding: 16, borderRadius: 14, background: "#FAFCFB", border: `1px solid ${ig.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <Badge tone="emerald">AI draft ready</Badge>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{plan.name}</div>
              </div>
              <div style={{ fontSize: 12, color: ig.muted, marginBottom: 14, lineHeight: 1.55 }}>{plan.description}</div>
              <div style={{ display: "grid", gap: 8 }}>
                {plan.nodes.map((n, i) => (
                  <div key={n.id} data-testid={`ai-node-${i}`} style={{
                    display: "flex", gap: 10, alignItems: "center", padding: "9px 12px",
                    borderRadius: 10, background: ig.card, border: `1px solid ${ig.border}`,
                  }}>
                    <span style={{
                      width: 24, height: 24, borderRadius: 7, display: "grid", placeItems: "center",
                      background: n.kind === "trigger" ? "#DCFCE7" : "#EDE9FE",
                      color: n.kind === "trigger" ? "#15803D" : "#6D28D9", fontSize: 11, fontWeight: 800,
                    }}>{i + 1}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 800, color: ig.ink }}>{n.label}</div>
                      <div style={{ fontSize: 11.5, color: ig.muted, marginTop: 1 }}>{n.summary}</div>
                    </div>
                    <Badge tone="neutral">{n.type}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: "14px 22px", borderTop: `1px solid ${ig.border}`, display: "flex", gap: 10 }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <div style={{ flex: 1 }} />
          {plan ? (
            <>
              <button onClick={() => { setPlan(null); }} data-testid="ai-regen" style={btnGhost}>Refine prompt</button>
              <button data-testid="ai-create" onClick={createWorkflow} disabled={creating} style={btnPrimary}>
                {creating ? "Creating…" : "Create workflow"} <ChevronRight size={14} />
              </button>
            </>
          ) : (
            <button data-testid="ai-generate" onClick={generate} disabled={busy || !prompt.trim()} style={btnPrimary}>
              <Sparkles size={13} /> {busy ? "Drafting…" : "Generate"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const iconBtn = {
  width: 30, height: 30, borderRadius: 8, border: `1px solid ${ig.border}`, background: ig.card,
  display: "inline-grid", placeItems: "center", cursor: "pointer", color: ig.ink,
};
const btnGhost = {
  padding: "9px 14px", borderRadius: 9, border: `1px solid ${ig.border}`,
  background: ig.card, color: ig.ink, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
};
const btnPrimary = {
  padding: "9px 16px", borderRadius: 9, border: 0,
  background: "linear-gradient(135deg, #7C3AED, #C084FC)", color: "#fff",
  fontSize: 13, fontWeight: 800, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 6,
  boxShadow: "0 6px 14px rgba(124,58,237,0.32)",
};
